import type { DetectedFormat } from './types';

/**
 * 零依赖的文件头嗅探。
 *
 * 只判断「内容是什么」，不关心扩展名与 MIME——扩展名与 MIME 的可信度由 file-detect 仲裁。
 * 全部判据均为确定性规则，便于单测；无法判定时返回 null（由调用方决定是否降级处理）。
 */

const TEXT_SNIFF_LIMIT = 64 * 1024;

function matches(bytes: Uint8Array, signature: readonly number[], offset = 0): boolean {
  if (bytes.length < offset + signature.length) return false;
  for (let i = 0; i < signature.length; i += 1) {
    if (bytes[offset + i] !== signature[i]) return false;
  }
  return true;
}

function asciiAt(bytes: Uint8Array, offset: number, length: number): string {
  if (bytes.length < offset + length) return '';
  let out = '';
  for (let i = 0; i < length; i += 1) out += String.fromCharCode(bytes[offset + i]);
  return out;
}

function decodeText(bytes: Uint8Array): string | null {
  const head = bytes.subarray(0, Math.min(bytes.length, TEXT_SNIFF_LIMIT));
  for (let i = 0; i < head.length; i += 1) {
    if (head[i] === 0) return null;
  }
  const decoded = new TextDecoder('utf-8', { fatal: false }).decode(head);
  const replacements = (decoded.match(/\uFFFD/g) ?? []).length;
  if (decoded.length > 0 && replacements / decoded.length > 0.02) return null;
  return decoded;
}

function binarySniff(bytes: Uint8Array, tail?: Uint8Array): DetectedFormat | null {
  if (matches(bytes, [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a])) {
    return {
      extension: 'png',
      mime: 'image/png',
      category: 'image',
      confidence: 'high',
      evidence: ['PNG 文件头 89 50 4E 47'],
    };
  }
  if (matches(bytes, [0xff, 0xd8, 0xff])) {
    return {
      extension: 'jpg',
      mime: 'image/jpeg',
      category: 'image',
      confidence: 'high',
      evidence: ['JPEG 起始标记 FF D8 FF'],
    };
  }
  if (matches(bytes, [0x47, 0x49, 0x46, 0x38])) {
    const version = asciiAt(bytes, 4, 3);
    return {
      extension: 'gif',
      mime: 'image/gif',
      category: 'image',
      confidence: 'high',
      evidence: [`GIF 文件头 GIF8${version.startsWith('9') ? '9a' : '7a'}`],
    };
  }
  if (asciiAt(bytes, 0, 4) === 'RIFF' && asciiAt(bytes, 8, 4) === 'WEBP') {
    return {
      extension: 'webp',
      mime: 'image/webp',
      category: 'image',
      confidence: 'high',
      evidence: ['RIFF 容器 + WEBP 标识'],
    };
  }
  if (asciiAt(bytes, 4, 4) === 'ftyp') {
    const brand = asciiAt(bytes, 8, 4);
    if (brand === 'avif' || brand === 'avis') {
      return {
        extension: 'avif',
        mime: 'image/avif',
        category: 'image',
        confidence: 'high',
        evidence: [`ISO-BMFF ftyp brand=${brand}`],
      };
    }
  }
  if (asciiAt(bytes, 0, 4) === 'glTF') {
    return {
      extension: 'glb',
      mime: 'model/gltf-binary',
      category: 'model',
      confidence: 'high',
      evidence: ['glTF 二进制魔数'],
    };
  }
  if (matches(bytes, [0x25, 0x50, 0x44, 0x46, 0x2d])) {
    return {
      extension: 'pdf',
      mime: 'application/pdf',
      category: 'document',
      confidence: 'high',
      evidence: ['PDF 魔数 %PDF-'],
    };
  }
  if (matches(bytes, [0x1f, 0x8b])) {
    return {
      extension: 'gz',
      mime: 'application/gzip',
      category: 'archive',
      confidence: 'high',
      evidence: ['GZIP 魔数 1F 8B'],
    };
  }
  if (matches(bytes, [0x37, 0x7a, 0xbc, 0xaf, 0x27, 0x1c])) {
    return {
      extension: '7z',
      mime: 'application/x-7z-compressed',
      category: 'archive',
      confidence: 'high',
      evidence: ['7-Zip 魔数 37 7A BC AF 27 1C'],
    };
  }
  if (matches(bytes, [0x52, 0x61, 0x72, 0x21, 0x1a, 0x07])) {
    return {
      extension: 'rar',
      mime: 'application/vnd.rar',
      category: 'archive',
      confidence: 'high',
      evidence: ['RAR 魔数 Rar!'],
    };
  }
  if (asciiAt(bytes, 0, 3) === 'ply') {
    return {
      extension: 'ply',
      mime: 'application/ply',
      category: 'model',
      confidence: 'high',
      evidence: ['PLY 头部标识 ply'],
    };
  }
  if (matches(bytes, [0x50, 0x4b, 0x03, 0x04]) || matches(bytes, [0x50, 0x4b, 0x05, 0x06]) || matches(bytes, [0x50, 0x4b, 0x07, 0x08])) {
    return sniffZipSubtype(bytes, tail);
  }
  const ole2 = sniffOle2(bytes);
  if (ole2) return ole2;
  if (isBinaryStl(bytes)) {
    return {
      extension: 'stl',
      mime: 'model/stl',
      category: 'model',
      confidence: 'high',
      evidence: ['二进制 STL 三角面计数与文件长度自洽'],
    };
  }
  return null;
}

/** OLE2 复合文档（.doc/.xls/.ppt 的容器）：头 8 字节固定签名。 */
const OLE2_SIGNATURE: readonly number[] = [0xd0, 0xcf, 0x11, 0xe0, 0xa1, 0xb1, 0x1a, 0xe1];

function asciiBytes(text: string): Uint8Array {
  const out = new Uint8Array(text.length);
  for (let i = 0; i < text.length; i += 1) out[i] = text.charCodeAt(i) & 0xff;
  return out;
}

/** 在头部字节里查找子串（朴素扫描，头部截断到 64KB 时开销可忽略）。 */
function indexOfBytes(haystack: Uint8Array, needle: Uint8Array): number {
  if (needle.length === 0 || haystack.length < needle.length) return -1;
  outer: for (let i = 0; i <= haystack.length - needle.length; i += 1) {
    for (let j = 0; j < needle.length; j += 1) {
      if (haystack[i + j] !== needle[j]) continue outer;
    }
    return i;
  }
  return -1;
}

/** OLE2 目录项名以 UTF-16LE 存储，因此按 UTF-16LE 编码匹配流名。 */
function containsUtf16Le(bytes: Uint8Array, text: string): boolean {
  const pattern = new Uint8Array(text.length * 2);
  for (let i = 0; i < text.length; i += 1) {
    const code = text.charCodeAt(i);
    pattern[i * 2] = code & 0xff;
    pattern[i * 2 + 1] = (code >> 8) & 0xff;
  }
  return indexOfBytes(bytes, pattern) >= 0;
}

/**
 * OLE2 复合文档的子类型判定。
 *
 * 旧版 Word/Excel/PowerPoint 共用同一个 OLE2 头，只能靠目录项里的流名区分：
 * 命中流名给出 medium 置信度，命中失败返回中性的 `ole2`，由扩展名仲裁兜底。
 */
function sniffOle2(bytes: Uint8Array): DetectedFormat | null {
  if (!matches(bytes, OLE2_SIGNATURE)) return null;
  const evidence = ['OLE2 复合文档头 D0 CF 11 E0'];

  if (containsUtf16Le(bytes, 'WordDocument')) {
    return {
      extension: 'doc',
      mime: 'application/msword',
      category: 'document',
      confidence: 'medium',
      evidence: [...evidence, '目录项含 WordDocument 流'],
    };
  }
  if (containsUtf16Le(bytes, 'Workbook') || containsUtf16Le(bytes, 'Book')) {
    return {
      extension: 'xls',
      mime: 'application/vnd.ms-excel',
      category: 'document',
      confidence: 'medium',
      evidence: [...evidence, '目录项含 Workbook 流'],
    };
  }
  if (containsUtf16Le(bytes, 'PowerPoint Document')) {
    return {
      extension: 'ppt',
      mime: 'application/vnd.ms-powerpoint',
      category: 'document',
      confidence: 'medium',
      evidence: [...evidence, '目录项含 PowerPoint Document 流'],
    };
  }
  return {
    extension: 'ole2',
    mime: null,
    category: 'other',
    confidence: 'low',
    evidence: [...evidence, '未能从目录项识别具体文档类型'],
  };
}

/**
 * ZIP 容器子类型。
 *
 * OOXML（docx/xlsx/pptx）与 epub/3mf 都是 ZIP，唯一可靠的区分依据是内部条目名。
 * ZIP 的条目名以 UTF-8 明文存放，**出现两处**：每个条目的本地文件头（文件前部）与
 * 中央目录（文件末尾）。OPC 规范并不规定条目顺序，因此真实文档完全可能出现
 * 「首个条目是大体积媒体（docProps/thumbnail、word/media/…），关键条目名被推到 64KB 之后」
 * 的情形 —— 只扫头部就会把合法 docx 误判成通用 zip（表现为「MIME 与实际格式不符」误报，
 * 并让 `resolve` 退化成按扩展名兜底）。因此这里同时扫描头部窗口与尾部窗口（中央目录）。
 *
 * 无需解压，也不引入依赖。
 */
function sniffZipSubtype(bytes: Uint8Array, tail?: Uint8Array): DetectedFormat {
  const evidence = ['ZIP 本地文件头 50 4B 03 04'];
  const head = bytes.subarray(0, Math.min(bytes.length, TEXT_SNIFF_LIMIT));
  const tailWindow = tail ? tail.subarray(Math.max(0, tail.length - TEXT_SNIFF_LIMIT)) : null;
  /** 优先在头部命中；只在尾部（中央目录）命中说明条目顺序把关键项排到了后面 */
  const locate = (marker: string): 'head' | 'tail' | null => {
    const needle = asciiBytes(marker);
    if (indexOfBytes(head, needle) >= 0) return 'head';
    if (tailWindow && tailWindow !== head && indexOfBytes(tailWindow, needle) >= 0) return 'tail';
    return null;
  };
  const has = (marker: string) => locate(marker) !== null;
  const contentTypes = has('[Content_Types].xml');
  const tailEvidence = ['条目名来自中央目录（尾部 64KB）'];

  /** 关键标记只在尾部命中时补一条证据，便于排查「条目顺序反常」的文件 */
  const tailNote = (markers: string[]): string[] =>
    markers.some((marker) => locate(marker) === 'tail') ? tailEvidence : [];

  if (contentTypes && has('word/')) {
    return {
      extension: 'docx',
      mime: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      category: 'document',
      confidence: 'high',
      evidence: [...evidence, '含 [Content_Types].xml 与 word/ 条目', ...tailNote(['[Content_Types].xml', 'word/'])],
    };
  }
  if (contentTypes && has('xl/')) {
    return {
      extension: 'xlsx',
      mime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      category: 'document',
      confidence: 'high',
      evidence: [...evidence, '含 [Content_Types].xml 与 xl/ 条目', ...tailNote(['[Content_Types].xml', 'xl/'])],
    };
  }
  if (contentTypes && has('ppt/')) {
    return {
      extension: 'pptx',
      mime: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
      category: 'document',
      confidence: 'high',
      evidence: [...evidence, '含 [Content_Types].xml 与 ppt/ 条目', ...tailNote(['[Content_Types].xml', 'ppt/'])],
    };
  }
  if (has('mimetype') && has('application/epub+zip')) {
    return {
      extension: 'epub',
      mime: 'application/epub+zip',
      category: 'document',
      confidence: 'high',
      evidence: [...evidence, '含 mimetype=application/epub+zip', ...tailNote(['mimetype'])],
    };
  }
  if (has('3D/3dmodel.model')) {
    return {
      extension: '3mf',
      mime: 'model/3mf',
      category: 'model',
      confidence: 'high',
      evidence: [...evidence, '含 3D/3dmodel.model 条目', ...tailNote(['3D/3dmodel.model'])],
    };
  }
  return {
    extension: 'zip',
    mime: 'application/zip',
    category: 'archive',
    confidence: 'high',
    evidence,
  };
}

function readUint32LE(bytes: Uint8Array, offset: number): number {
  return (
    bytes[offset] | (bytes[offset + 1] << 8) | (bytes[offset + 2] << 16) | (bytes[offset + 3] << 24)
  ) >>> 0;
}

/** 二进制 STL：80 字节头 + 4 字节三角面数，文件长度应为 84 + 50 * n。 */
function isBinaryStl(bytes: Uint8Array): boolean {
  if (bytes.length < 84) return false;
  if (asciiAt(bytes, 0, 5).toLowerCase() === 'solid') return false;
  const triangles = readUint32LE(bytes, 80);
  if (triangles === 0 || triangles > 20_000_000) return false;
  return bytes.length >= 84 + triangles * 50 && bytes.length < 84 + triangles * 50 + 1000;
}

/** 结构性文本判定：Markdown / CSV / YAML / 纯文本。 */
function sniffText(bytes: Uint8Array): DetectedFormat | null {
  const text = decodeText(bytes);
  if (text === null) return null;

  const body = text.replace(/^\uFEFF/, '');
  const trimmed = body.trimStart();
  const lower = trimmed.toLowerCase();

  if (/^<!doctype html[\s>]/.test(lower) || /^<html[\s>]/.test(lower)) {
    return {
      extension: 'html',
      mime: 'text/html',
      category: 'document',
      confidence: 'high',
      evidence: ['HTML 文档声明或 <html> 根标签'],
    };
  }
  if (trimmed.startsWith('<?xml') || trimmed.startsWith('<')) {
    if (/<svg[\s>]/i.test(body.slice(0, 4096))) {
      return {
        extension: 'svg',
        mime: 'image/svg+xml',
        category: 'image',
        confidence: trimmed.startsWith('<?xml') ? 'high' : 'medium',
        evidence: ['文本内容包含 <svg> 根元素'],
      };
    }
    if (trimmed.startsWith('<?xml') || /^<[A-Za-z_][\w.:-]*(\s|>|\/)/.test(trimmed)) {
      return {
        extension: 'xml',
        mime: 'application/xml',
        category: 'document',
        confidence: 'medium',
        evidence: ['内容以 XML 标签起始'],
      };
    }
  }

  if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
    const parsed = safeJsonParse(trimmed);
    if (parsed !== undefined) {
      const root = parsed as Record<string, unknown> | null;
      const rootObject = root && typeof root === 'object' ? root : null;
      const asset = rootObject ? (rootObject.asset as Record<string, unknown> | undefined) : undefined;
      const looksLikeGltf =
        !!rootObject &&
        !!asset &&
        typeof asset === 'object' &&
        typeof asset.version === 'string' &&
        /^\d+\.\d+$/.test(asset.version) &&
        ('scenes' in rootObject || 'meshes' in rootObject || 'nodes' in rootObject);
      if (looksLikeGltf) {
        return {
          extension: 'gltf',
          mime: 'model/gltf+json',
          category: 'model',
          confidence: 'high',
          evidence: ['JSON 含 asset.glTF 字段'],
        };
      }
      return {
        extension: 'json',
        mime: 'application/json',
        category: 'document',
        confidence: 'high',
        evidence: ['内容可被 JSON.parse 解析'],
      };
    }
  }

  if (/^solid[\s\r\n]/i.test(trimmed) && /facet\s+normal/i.test(body)) {
    return {
      extension: 'stl',
      mime: 'model/stl',
      category: 'model',
      confidence: 'high',
      evidence: ['ASCII STL: solid 头 + facet normal'],
    };
  }
  if (looksLikeObj(body)) {
    return {
      extension: 'obj',
      mime: 'model/obj',
      category: 'model',
      confidence: 'medium',
      evidence: ['文本含 OBJ 顶点/面指令行'],
    };
  }
  if (looksLikeMarkdown(body)) {
    return {
      extension: 'md',
      mime: 'text/markdown',
      category: 'document',
      confidence: 'medium',
      evidence: ['文本含 Markdown 结构标记'],
    };
  }
  if (looksLikeCsv(body)) {
    return {
      extension: 'csv',
      mime: 'text/csv',
      category: 'document',
      confidence: 'medium',
      evidence: ['多行分隔符数量一致，符合分隔符文本'],
    };
  }
  if (looksLikeYaml(body)) {
    return {
      extension: 'yaml',
      mime: 'application/yaml',
      category: 'document',
      confidence: 'low',
      evidence: ['文本为键值对/列表结构'],
    };
  }
  return {
    extension: 'txt',
    mime: 'text/plain',
    category: 'document',
    confidence: 'low',
    evidence: ['内容为合法 UTF-8 文本'],
  };
}

function safeJsonParse(text: string): unknown {
  try {
    return JSON.parse(text) as unknown;
  } catch {
    return undefined;
  }
}

function looksLikeObj(text: string): boolean {
  const lines = text.split(/\r?\n/).slice(0, 200);
  const vertex = lines.filter((line) => /^v\s+-?\d/.test(line)).length;
  const face = lines.filter((line) => /^f\s+\d/.test(line)).length;
  const markers = lines.filter((line) => /^(vn|vt|usemtl|mtllib|g|o|s)\s/.test(line)).length;
  return vertex >= 3 && (face >= 1 || markers >= 1);
}

function looksLikeMarkdown(text: string): boolean {
  const lines = text.split(/\r?\n/).slice(0, 200);
  const kinds = new Set<string>();
  for (const line of lines) {
    if (/^#{1,6}\s+\S/.test(line)) kinds.add('heading');
    else if (/^```/.test(line)) kinds.add('fence');
    else if (/^>\s+\S/.test(line)) kinds.add('quote');
    else if (/^(\s*)[-*+]\s+\S/.test(line)) kinds.add('bullet');
    else if (/\*\*[^*\n]+\*\*/.test(line)) kinds.add('bold');
    else if (/\[[^\]\n]+\]\([^)\n]+\)/.test(line)) kinds.add('link');
  }
  return kinds.size >= 2;
}

function looksLikeCsv(text: string): boolean {
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .slice(0, 6);
  if (lines.length < 2) return false;
  for (const delimiter of [',', '\t', ';', '|']) {
    const counts = lines.map((line) => line.split(delimiter).length);
    if (counts[0] >= 2 && counts.every((count) => count === counts[0])) return true;
  }
  return false;
}

function looksLikeYaml(text: string): boolean {
  const lines = text
    .split(/\r?\n/)
    .filter((line) => line.trim().length > 0 && !line.trimStart().startsWith('#'))
    .slice(0, 40);
  if (lines.length === 0) return false;
  let hits = 0;
  for (const line of lines) {
    if (/^\s*[\w"'][\w ."'-]*:\s*(\S|$)/.test(line)) hits += 1;
    else if (/^\s*-\s+(\S|$)/.test(line)) hits += 1;
  }
  return hits >= 2 && hits / lines.length >= 0.6;
}

/** 嗅探文件真实格式；无法判定时返回 null。 */
/**
 * 嗅探「内容是什么」。
 *
 * @param bytes 文件头部字节（≥ 64KB 覆盖全部判据）
 * @param tail  可选的文件尾部字节（ZIP 的中央目录在这里；条目顺序反常的 OOXML 只靠头部会漏判）
 */
export function sniffFormat(bytes: Uint8Array, tail?: Uint8Array): DetectedFormat | null {
  if (bytes.length === 0) return null;
  return binarySniff(bytes, tail) ?? sniffText(bytes);
}
